import { GridRenderCellParams } from '@mui/x-data-grid';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { copyPreset, editPreset } from '../../../../services/sealing/android';
import { RootState } from '../../../../store';
import DataTable from '../../../DataDisplay/Table';
import { showDialog, showToast } from '../../../utils/notifications';
import ActionMenu from './PresetAction';
import PresetDrawer from './PresetDrawer';
import { convertKeysToSnakeCase, createPresetOptions } from './PresetFunction';

import moment from 'moment';

interface PresetTableProps {
    searchValue: string;
    onSave: (data: any) => void;
    onDelete: (id: string, presetName: string) => void;
    preset: any;
    setPreset: any;
}

const PresetTable: React.FC<PresetTableProps> = ({ searchValue, onDelete, preset, setPreset }) => {
    const { t } = useTranslation();
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [selectedPreset, setSelectedPreset] = useState<any | null>(null);
    const [view, setView] = useState(false);
    const [copy, setCopy] = useState(false);
    const authContext = useSelector((state: RootState) => state.mainReducer.authContext);
    const serviceables = useSelector((state: RootState) => state.androidSealingReducer.serviceables);
    const userDetails = useSelector((state: RootState) => state.mainReducer.userDetails);
    const region = useSelector((state: RootState) => state.mainReducer.region);
    const isBetaAccount = authContext?.is_beta_account;
    const isOwner = authContext?.role === 'Owner' || authContext?.role === 'SubOwner';
    const isManager = authContext?.is_group_manager;

    const handleViewClick = (data: any) => {
        const skipKeys = ['presetName', 'presetDescription'];
        const filteredPreset = convertKeysToSnakeCase(data, skipKeys);
        setSelectedPreset(filteredPreset);
        setView(true);
        setCopy(false);
        setTimeout(() => {
            setDrawerOpen(true);
        }, 1000);
    };

    const handleEditClick = (data: any) => {
        const skipKeys = ['presetName', 'presetDescription'];
        const filteredPreset = convertKeysToSnakeCase(data, skipKeys);
        setSelectedPreset(filteredPreset);
        setView(false);
        setCopy(false);
        setTimeout(() => {
            setDrawerOpen(true);
        }, 1000);
    };

    const handleDeleteClick = (id: string, presetName: string) => {
        showDialog({
            title: t('delete_preset'),
            content: t(`delete_preset_details`, { preset_name: presetName }),
            onOk: () => onDelete(id, presetName),
            okText: t('yes'),
            cancelText: t('no'),
        });
    };

    const handleDrawerClose = () => {
        setDrawerOpen(false);
        setTimeout(() => {
            setSelectedPreset(null);
        }, 1000);
    };
    const handleSave = (values: any) => {
        if (copy) {
            handleCopy(values);
        } else {
            handleEdit(values);
        }
    };
    const handleEdit = async (values: any) => {
        const data = values;
        const args = createPresetOptions(data, userDetails, selectedPreset?.id);

        const response = await editPreset(args, authContext.token, region);
        if (response.result.code === '0000') {
            const updatedData = preset.map((item: any) => (item.id === response.data.id ? response.data : item));
            setPreset(updatedData);
            showToast('success', t('edited_preset_successfully'));
            handleDrawerClose();
        } else if (response?.result?.code === '14000') {
            showToast('error', t('SEALING_PRESET_NAME_IS_DUPLICATED'));
        } else {
            showToast('error', t('try_again_later'));
        }
    };

    const handleCopyClick = async (data: any) => {
        const skipKeys = ['presetName', 'presetDescription'];
        const filteredPreset = convertKeysToSnakeCase(data, skipKeys);
        setSelectedPreset(filteredPreset);
        setCopy(true);
        setView(false);
        setTimeout(() => {
            setDrawerOpen(true);
        }, 1000);
    };

    const handleCopy = async (values: any) => {
        const data = values;
        const args = {
            presetName: data.presetName,
            groupId: data.groupId.groupId,
        };
        const presetId = selectedPreset.id;
        const response = await copyPreset(args, presetId, authContext.token, region);
        if (response.result.code === '0000') {
            setPreset([response.data, ...preset]);
            showToast('success', t('copied_preset_successfully'));
            handleDrawerClose();
        } else if (response?.result?.code === '14000') {
            showToast('error', t('SEALING_PRESET_NAME_IS_DUPLICATED'));
        } else {
            showToast('error', t('try_again_later'));
        }
    };
    const columns = [
        {
            headerName: t('preset_name'),
            field: 'presetName',
            sortable: true,
            disableColumnMenu: true,
            flex: 1,
        },
        {
            headerName: t('created_at'),
            field: 'createdAt',
            sortable: true,
            disableColumnMenu: true,
            flex: 1,
            renderCell: (params: GridRenderCellParams) => {
                return moment(params.row.createdAt).format('YYYY-MM-DD HH:mm:ss');
            },
        },
        {
            headerName: t('description'),
            field: 'presetDescription',
            flex: 1,
            sortable: false,
            disableColumnMenu: true,
        },
        {
            headerName: t('action'),
            field: 'action',
            flex: 0.3,
            sortable: false,
            disableColumnMenu: true,
            renderCell: (params: GridRenderCellParams) => {
                return (
                    <ActionMenu
                        row={params.row}
                        onView={handleViewClick}
                        onCopy={handleCopyClick}
                        onEdit={handleEditClick}
                        onDelete={handleDeleteClick}
                        disabled={isBetaAccount ? !isOwner && !isManager : false}
                        isBetaAccount={isBetaAccount}
                    />
                );
            },
        },
    ];

    const filterData = (data: any[]) => {
        if (!searchValue) return data;
        const lowerSearchValue = searchValue.toLowerCase();
        return data.filter((item) => item.presetName && item.presetName.toLowerCase().includes(lowerSearchValue));
    };

    return (
        <>
            <DataTable height={'80%'} columns={columns} rows={filterData(preset) || []} pageSize={10} pageSizeOptions={[10, 15, 20, 30, 50]} />
            {selectedPreset && <PresetDrawer open={drawerOpen} onSave={handleSave} onClose={handleDrawerClose} preset={selectedPreset} view={view} copy={copy} />}
        </>
    );
};

export default PresetTable;
